import React, { useEffect, useMemo, useState } from "react";
import { useDesktopNav } from "./navigation/DesktopNavContext";
import { DesktopBackLink } from "./navigation/ScreenBackButton";
import { supabase } from "../supabase";
import { formatHoursMinutes } from "../helpers/timeHelpers";
import {
  PROJECT_ANALYTICS_SORT_OPTIONS,
  buildProjectAnalyticsReport,
  computeReportGrandTotals,
  filterProjectsForAnalytics,
  sortProjectAnalyticsRows,
} from "../helpers/projectAnalytics";
import {
  enrichAnalyticsRowWithFinancials,
  loadFinancialMetricsForAnalytics,
} from "../helpers/projectFinancialReport";
import { usePermissions } from "../hooks/usePermissions";
import { CAPABILITY } from "../helpers/permissions";
import { loadScopedActiveProjects } from "../helpers/operationalQueryAdapters";
import { applyOrganizationScopeForActor } from "../helpers/orgQueryScope";
import {
  applyOperationalTimeLogOrgScope,
  applyUserIdsToQuery,
  deriveClientsFromProjects,
  resolveOperationalOrganizationId,
  resolveScopedUserIdsForQuery,
  selectOperationalTimeLogs,
} from "../helpers/scopedQueries";
import { shouldSkipSupabase } from "../helpers/guestMode";
import { CPDropdown } from "./CPDropdown";
import { CPInput } from "./CPInput";
import { useIsDesktopViewport } from "../hooks/useIsDesktopViewport";
import { ProjectAnalyticsDesktop } from "./intelligence/IntelligenceDesktop";

function getDefaultDateRange() {
  const today = new Date();
  const start = new Date(today.getFullYear(), today.getMonth(), 1);

  return {
    startDate: start.toISOString().split("T")[0],
    endDate: today.toISOString().split("T")[0],
  };
}

function formatCurrencyCompact(amount) {
  return `$${Math.round(Number(amount || 0)).toLocaleString("en-CA")}`;
}

function formatCurrencyFull(amount) {
  return `$${Number(amount || 0).toFixed(2)}`;
}

export function ProjectAnalyticsScreen({
  onBack,
  currentUser,
  onOpenProjectDetail,
}) {
  const desktopNav = useDesktopNav();
  const isDesktop = useIsDesktopViewport();
  const { directReportIds } = usePermissions();
  const defaults = getDefaultDateRange();

  const [startDate, setStartDate] = useState(defaults.startDate);
  const [endDate, setEndDate] = useState(defaults.endDate);
  const [projectStatus, setProjectStatus] = useState("active");
  const [clientId, setClientId] = useState("");
  const [projectId, setProjectId] = useState("");
  const [sortKey, setSortKey] = useState("highest_total_cost");

  const [allProjects, setAllProjects] = useState([]);
  const [reportRows, setReportRows] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);

  const sortedReportRows = useMemo(
    () =>
      reportRows ? sortProjectAnalyticsRows(reportRows, sortKey) : null,
    [reportRows, sortKey],
  );

  useEffect(() => {
    loadProjects();
  }, [currentUser?.supabase_user_id, directReportIds]);

  async function loadProjects() {
    if (shouldSkipSupabase(currentUser)) {
      setAllProjects([]);
      return;
    }

    const projectSelect = `
        project_id,
        project_name,
        project_number,
        client_id,
        status,
        is_active,
        created_by,
        clients ( client_name )
      `;

    try {
      const data = await loadScopedActiveProjects(
        supabase,
        currentUser,
        { directReportIds },
        projectSelect,
        { activeOnly: false },
      );
      setAllProjects(data || []);
    } catch (error) {
      console.error("PROJECT LOAD ERROR:", error);
    }
  }

  const clients = useMemo(
    () => deriveClientsFromProjects(allProjects),
    [allProjects],
  );

  const filterOptions = useMemo(
    () => ({ projectStatus, clientId, projectId }),
    [projectStatus, clientId, projectId],
  );

  const projectOptions = useMemo(
    () =>
      filterProjectsForAnalytics(allProjects, {
        projectStatus,
        clientId,
        projectId: "",
      }),
    [allProjects, projectStatus, clientId],
  );

  useEffect(() => {
    if (
      clientId &&
      !clients.some((client) => client.client_id === clientId)
    ) {
      setClientId("");
    }
  }, [clientId, clients]);

  useEffect(() => {
    if (
      projectId &&
      !projectOptions.some((project) => project.project_id === projectId)
    ) {
      setProjectId("");
    }
  }, [projectId, projectOptions]);

  async function handleGenerateReport() {
    if (!startDate || !endDate) {
      alert("Select a start and end date.");
      return;
    }

    setIsGenerating(true);
    setReportRows(null);

    if (shouldSkipSupabase(currentUser)) {
      setReportRows([]);
      setIsGenerating(false);
      return;
    }

    try {
      const filteredProjects = filterProjectsForAnalytics(
        allProjects,
        filterOptions,
      );
      const filteredProjectIds = filteredProjects.map(
        (project) => project.project_id,
      );

      if (filteredProjectIds.length === 0) {
        setReportRows([]);
        setIsGenerating(false);
        return;
      }

      let timeLogsQuery = selectOperationalTimeLogs(
        supabase,
        "project_id, project_name, date_iso, total_hours, net_work_seconds, hourly_rate",
      )
        .gte("date_iso", startDate)
        .lte("date_iso", endDate)
        .in("project_id", filteredProjectIds);

      const timelogUserIds = await resolveScopedUserIdsForQuery(
        supabase,
        currentUser,
        CAPABILITY.TIMELOG_VIEW,
        { directReportIds },
      );
      const organizationId = await resolveOperationalOrganizationId(
        currentUser,
        supabase,
        { directReportIds },
      );

      timeLogsQuery = applyUserIdsToQuery(timeLogsQuery, timelogUserIds);
      timeLogsQuery = applyOperationalTimeLogOrgScope(timeLogsQuery, organizationId);

      const { data: timeLogs, error: timeLogsError } = await timeLogsQuery;

      if (timeLogsError) {
        throw timeLogsError;
      }

      let materialQuery = supabase
        .from("project_material_usage")
        .select("project_id, usage_date, total_cost, entered_by")
        .gte("usage_date", startDate)
        .lte("usage_date", endDate)
        .in("project_id", filteredProjectIds)
        .is("voided_at", null);

      // Keep analytics materials on the same personnel scope as labour.
      // Project Summary / Detail use project-ledger aggregation instead.
      materialQuery = applyUserIdsToQuery(materialQuery, timelogUserIds, "entered_by");
      materialQuery = await applyOrganizationScopeForActor(
        materialQuery,
        currentUser,
        supabase,
        { tableName: "project_material_usage" },
      );

      const { data: materialUsage, error: materialError } = await materialQuery;

      if (materialError) {
        throw materialError;
      }

      const otherPeriodEnd = new Date(`${endDate}T00:00:00.000Z`);
      otherPeriodEnd.setUTCDate(otherPeriodEnd.getUTCDate() + 1);

      let otherQuery = supabase
        .from("project_other_costs")
        .select("project_id, amount, is_active, created_at")
        .in("project_id", filteredProjectIds)
        .eq("is_active", true)
        .gte("created_at", `${startDate}T00:00:00.000Z`)
        .lt("created_at", otherPeriodEnd.toISOString());

      otherQuery = await applyOrganizationScopeForActor(
        otherQuery,
        currentUser,
        supabase,
        { tableName: "project_other_costs" },
      );

      const { data: otherCosts, error: otherError } = await otherQuery;

      if (otherError) {
        throw otherError;
      }

      const rows = buildProjectAnalyticsReport({
        projects: filteredProjects,
        timeLogs: timeLogs || [],
        materialUsage: materialUsage || [],
        otherCosts: otherCosts || [],
      });

      const financialMetrics = await loadFinancialMetricsForAnalytics(
        currentUser,
        filteredProjectIds,
        { startDate, endDate },
      );

      const enrichedRows = rows.map((row) =>
        enrichAnalyticsRowWithFinancials(row, {
          quoteSummary: financialMetrics.summariesByProject.get(row.project_id),
          quoteLines: financialMetrics.linesByProject.get(row.project_id) || [],
          billedLineItems:
            financialMetrics.billedLinesByProject.get(row.project_id) || [],
        }),
      );

      setReportRows(enrichedRows);
    } catch (err) {
      console.error("PROJECT ANALYTICS ERROR:", err);
      alert("Unable to generate project analytics report.");
    } finally {
      setIsGenerating(false);
    }
  }

  const grandTotals = useMemo(
    () => computeReportGrandTotals(sortedReportRows || []),
    [sortedReportRows],
  );

  if (isDesktop) {
    return (
      <ProjectAnalyticsDesktop
        onBack={onBack}
        startDate={startDate}
        endDate={endDate}
        onStartDate={setStartDate}
        onEndDate={setEndDate}
        projectStatus={projectStatus}
        onProjectStatus={setProjectStatus}
        clientId={clientId}
        onClient={setClientId}
        clients={clients}
        projectId={projectId}
        onProject={setProjectId}
        projectOptions={projectOptions}
        sortKey={sortKey}
        onSort={setSortKey}
        sortOptions={PROJECT_ANALYTICS_SORT_OPTIONS}
        onGenerate={handleGenerateReport}
        isGenerating={isGenerating}
        rows={sortedReportRows}
        grandTotals={grandTotals}
        onOpenProjectDetail={onOpenProjectDetail}
      />
    );
  }

  return (
    <div className="intel-screen">
      <div className="cockpit-header">
        {desktopNav ? (<DesktopBackLink onClick={onBack} label={desktopNav.parentLabel} />) : (
        <button
          className="cockpit-back-btn"
          onClick={onBack}
          aria-label="Back to Reports & Analytics"
        >
          Back
        </button>
        )}

        <div style={{ marginTop: 50 }}>
          <div className="cockpit-title">PROJECT ANALYTICS</div>
          <div
            style={{
              color: "#cfd8ea",
              marginTop: 6,
              fontSize: 14,
            }}
          >
            Hours, labour cost, material cost, quoted, billed, and margin by project
          </div>
        </div>
      </div>

      <div className="rb-card">
        <div className="rb-field-group">
          <CPInput
            label="Start Date"
            type="date"
            value={startDate}
            onChange={setStartDate}
          />
        </div>

        <div className="rb-field-group">
          <CPInput
            label="End Date"
            type="date"
            value={endDate}
            onChange={setEndDate}
          />
        </div>

        <div className="rb-field-group">
          <CPDropdown
            label="Projects"
            value={projectStatus}
            onChange={setProjectStatus}
            includePlaceholder={false}
            options={[
              { value: "active", label: "Active Projects" },
              { value: "archived", label: "Archived Projects" },
              { value: "all", label: "All Projects" },
            ]}
          />
        </div>

        <div className="rb-field-group">
          <CPDropdown
            label="Client"
            value={clientId}
            onChange={setClientId}
            options={[
              { value: "", label: "All Clients" },
              ...clients.map((client) => ({
                value: client.client_id,
                label: client.client_name,
              })),
            ]}
          />
        </div>

        <div className="rb-field-group">
          <CPDropdown
            label="Project"
            value={projectId}
            onChange={setProjectId}
            options={[
              { value: "", label: "All Projects" },
              ...projectOptions.map((project) => ({
                value: project.project_id,
                label: project.project_name,
              })),
            ]}
          />
        </div>

        <div className="rb-field-group">
          <CPDropdown
            label="Sort"
            value={sortKey}
            onChange={setSortKey}
            includePlaceholder={false}
            options={PROJECT_ANALYTICS_SORT_OPTIONS}
          />
        </div>

        <button
          className="rb-generate-btn"
          onClick={handleGenerateReport}
          disabled={!startDate || !endDate || isGenerating}
        >
          {isGenerating ? "Generating..." : "Generate Report"}
        </button>
      </div>

      {sortedReportRows && (
        <div className="rb-card" style={{ marginTop: 20 }}>
          <div
            style={{
              color: "var(--nx-gold)",
              fontSize: 12,
              textTransform: "uppercase",
              letterSpacing: "1px",
            }}
          >
            Report Summary
          </div>

          <div
            style={{
              marginTop: 12,
              color: "#cfd8ea",
              fontSize: 14,
            }}
          >
            {new Date(`${startDate}T12:00:00`).toLocaleDateString("en-CA", {
              month: "short",
              day: "numeric",
              year: "numeric",
            })}
            {" → "}
            {new Date(`${endDate}T12:00:00`).toLocaleDateString("en-CA", {
              month: "short",
              day: "numeric",
              year: "numeric",
            })}
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
              gap: 12,
              marginTop: 16,
            }}
          >
            <div style={{ color: "white" }}>
              <div style={{ opacity: 0.7, fontSize: 12 }}>Projects</div>
              <div style={{ fontWeight: 700, fontSize: 20 }}>
                {grandTotals.projectCount}
              </div>
            </div>
            <div style={{ color: "white" }}>
              <div style={{ opacity: 0.7, fontSize: 12 }}>Total Hours</div>
              <div style={{ fontWeight: 700, fontSize: 20 }}>
                {formatHoursMinutes(grandTotals.totalHours)}
              </div>
            </div>
            <div style={{ color: "white" }}>
              <div style={{ opacity: 0.7, fontSize: 12 }}>Labour Cost</div>
              <div style={{ fontWeight: 700, fontSize: 20, color: "#d4af37" }}>
                {formatCurrencyCompact(grandTotals.labourCost)}
              </div>
            </div>
            <div style={{ color: "white" }}>
              <div style={{ opacity: 0.7, fontSize: 12 }}>Material Cost</div>
              <div style={{ fontWeight: 700, fontSize: 20, color: "#d4af37" }}>
                {formatCurrencyCompact(grandTotals.materialCost)}
              </div>
            </div>
            <div style={{ color: "white" }}>
              <div style={{ opacity: 0.7, fontSize: 12 }}>Other Costs</div>
              <div style={{ fontWeight: 700, fontSize: 20, color: "#d4af37" }}>
                {formatCurrencyCompact(grandTotals.otherCost)}
              </div>
            </div>
          </div>

          <div
            style={{
              marginTop: 16,
              paddingTop: 16,
              borderTop: "1px solid rgba(255,255,255,0.08)",
              display: "grid",
              gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
              gap: 12,
            }}
          >
            <div style={{ color: "white" }}>
              <div style={{ opacity: 0.7, fontSize: 12 }}>Quoted (snapshot)</div>
              <div style={{ fontWeight: 700, fontSize: 20, color: "#c9a84c" }}>
                {formatCurrencyCompact(grandTotals.quoted)}
              </div>
            </div>
            <div style={{ color: "white" }}>
              <div style={{ opacity: 0.7, fontSize: 12 }}>Billed (period)</div>
              <div style={{ fontWeight: 700, fontSize: 20, color: "#c9a84c" }}>
                {formatCurrencyCompact(grandTotals.billed)}
              </div>
            </div>
          </div>

          <div
            style={{
              marginTop: 16,
              paddingTop: 16,
              borderTop: "1px solid rgba(255,255,255,0.08)",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              fontWeight: 700,
            }}
          >
            <span style={{ color: "white" }}>Total Project Cost (period)</span>
            <span style={{ color: "#4ade80", fontSize: 24 }}>
              {formatCurrencyCompact(grandTotals.totalCost)}
            </span>
          </div>

          <div
            style={{
              marginTop: 12,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              fontWeight: 700,
            }}
          >
            <span style={{ color: "white" }}>Margin (Billed − Actual, period)</span>
            <span
              style={{
                color: grandTotals.margin >= 0 ? "#4ade80" : "#e06b5a",
                fontSize: 20,
              }}
            >
              {formatCurrencyCompact(grandTotals.margin)}
            </span>
          </div>

          <div
            style={{
              marginTop: 24,
              color: "var(--nx-text-2)",
              textTransform: "uppercase",
              fontSize: 12,
              letterSpacing: "1px",
            }}
          >
            Projects
          </div>

          {sortedReportRows.length === 0 ? (
            <p
              style={{
                marginTop: 16,
                opacity: 0.7,
                color: "white",
              }}
            >
              No project activity found for the selected filters.
            </p>
          ) : (
            sortedReportRows.map((row) => (
              <button
                key={row.project_id}
                type="button"
                onClick={() =>
                  onOpenProjectDetail?.({
                    project_id: row.project_id,
                    name: row.project_name,
                    hrs: row.totalHours,
                  })
                }
                style={{
                  display: "block",
                  width: "100%",
                  marginTop: 12,
                  padding: 16,
                  borderRadius: 14,
                  border: "1px solid rgba(201,168,76,0.25)",
                  background: "rgba(255,255,255,0.04)",
                  color: "white",
                  textAlign: "left",
                  cursor: onOpenProjectDetail ? "pointer" : "default",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    gap: 12,
                    alignItems: "flex-start",
                  }}
                >
                  <div>
                    <div
                      style={{
                        fontSize: 18,
                        fontWeight: 600,
                        marginBottom: 4,
                      }}
                    >
                      {row.project_name}
                    </div>
                    {row.client_name && (
                      <div style={{ opacity: 0.7, fontSize: 13 }}>
                        {row.client_name}
                      </div>
                    )}
                  </div>
                  <div
                    style={{
                      color: "#d4af37",
                      fontWeight: 700,
                      fontSize: 18,
                      flexShrink: 0,
                    }}
                  >
                    {formatCurrencyCompact(row.totalCost)}
                  </div>
                </div>

                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
                    gap: 10,
                    marginTop: 12,
                    fontSize: 13,
                  }}
                >
                  <div>
                    <div style={{ opacity: 0.65 }}>Hours</div>
                    <div style={{ fontWeight: 600 }}>
                      {formatHoursMinutes(row.totalHours)}
                    </div>
                  </div>
                  <div>
                    <div style={{ opacity: 0.65 }}>Actual</div>
                    <div style={{ fontWeight: 600 }}>
                      {formatCurrencyCompact(row.totalCost)}
                    </div>
                  </div>
                  <div>
                    <div style={{ opacity: 0.65 }}>Billed</div>
                    <div style={{ fontWeight: 600 }}>
                      {formatCurrencyCompact(row.billed || 0)}
                    </div>
                  </div>
                </div>

                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
                    gap: 10,
                    marginTop: 10,
                    fontSize: 13,
                  }}
                >
                  <div>
                    <div style={{ opacity: 0.65 }}>Quoted</div>
                    <div style={{ fontWeight: 600 }}>
                      {row.hasQuoteSnapshot
                        ? formatCurrencyCompact(row.quoted || 0)
                        : "—"}
                    </div>
                  </div>
                  <div>
                    <div style={{ opacity: 0.65 }}>Margin</div>
                    <div
                      style={{
                        fontWeight: 600,
                        color:
                          Number(row.margin || 0) >= 0 ? "#4ade80" : "#e06b5a",
                      }}
                    >
                      {formatCurrencyCompact(row.margin || 0)}
                    </div>
                  </div>
                  <div>
                    <div style={{ opacity: 0.65 }}>Labour / Material</div>
                    <div style={{ fontWeight: 600, fontSize: 12 }}>
                      {formatCurrencyCompact(row.labourCost)} /{" "}
                      {formatCurrencyCompact(row.materialCost)}
                    </div>
                  </div>
                </div>

                <div
                  style={{
                    marginTop: 10,
                    fontSize: 12,
                    opacity: 0.55,
                  }}
                >
                  Labour {formatCurrencyFull(row.labourCost)} · Material{" "}
                  {formatCurrencyFull(row.materialCost)} · Other{" "}
                  {formatCurrencyFull(row.otherCost)}
                  {row.hasQuoteSnapshot
                    ? ` · Quoted ${formatCurrencyFull(row.quoted || 0)}`
                    : ""}
                  {onOpenProjectDetail ? " · View detail →" : ""}
                </div>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}
